import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface PriceAlert {
  id: string;
  user_id: string;
  gpu_id: string;
  target_price: number;
  is_active: boolean;
  triggered_at: string | null;
  created_at: string;
}

export const usePriceAlerts = () => {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState<PriceAlert[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchAlerts();
    } else {
      setAlerts([]);
      setLoading(false);
    }
  }, [user]);

  const fetchAlerts = async () => {
    if (!user) return;
    
    setLoading(true);
    const { data, error } = await supabase
      .from('price_alerts')
      .select('*')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .order('created_at', { ascending: false });
    
    if (!error && data) {
      setAlerts(data);
    }
    setLoading(false);
  };

  const createAlert = async (gpuId: string, targetPrice: number) => {
    if (!user) return { error: new Error('Not authenticated') };
    
    const { data, error } = await supabase
      .from('price_alerts')
      .insert({
        user_id: user.id,
        gpu_id: gpuId,
        target_price: targetPrice,
      })
      .select()
      .single();
    
    if (!error && data) {
      setAlerts(prev => [data, ...prev]);
    }
    
    return { data, error };
  };

  const deleteAlert = async (alertId: string) => {
    const { error } = await supabase
      .from('price_alerts')
      .delete()
      .eq('id', alertId);
    
    if (!error) {
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    }
    
    return { error };
  };

  const getAlertForGPU = (gpuId: string) => {
    return alerts.find(a => a.gpu_id === gpuId && a.is_active);
  };

  return {
    alerts,
    loading,
    createAlert,
    deleteAlert,
    getAlertForGPU,
    refetch: fetchAlerts,
  };
};
